import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { ENTITY_TYPE, useGenericBi } from '../../../lib/use-generic-bi';
import type { PiledUpJob } from '../../../lib/piled-up-jobs';
import { Card, TextButton, Typography } from '../ui';
import { HorizontalScroller } from '../HorizontalScroller';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { TaskDetailModal } from '../tasks/TaskDetailModal';
import { PiledUpAvatar } from './PiledUpAvatar';
import { PiledUpDeck } from './PiledUpDeck';
import styles from './PiledUpJobs.module.css';

// "N jobs piled up while you slept" — the work the agents did while the user was away. Two renderings of
// the SAME data over the SAME shared logic (`lib/piled-up-jobs.ts`):
//
//   • wide  → a horizontal STRIP of cards, sized for the desktop home band (Figma 12228:150815). When
//             the band also carries the connect-tools callout the strip's track is a third narrower, so
//             the SAME strip PILES its cards instead — each one overlapping the last by a little over
//             half, front card on the trailing edge (Figma 15545:337594). See `piled`.
//   • narrow (mobile) → a rotating swipe DECK under a bold "N Jobs piled up…" header (Figma 9112:55884).
//
// The narrow deck matches the phone frame; the strip stays on the desktop band. The switch is the same
// moment the band restacks to a single column — and `narrow` is therefore passed DOWN from
// `CatchUpSections`, which owns that measurement, rather than re-derived from a `matchMedia` query
// here. The band stacks on a `@container` query (its own width), and the width that narrows it is most
// often the docked chat, which does not change `window.innerWidth`: a viewport query here reported
// "wide" while the row around it had already stacked, leaving the full-width desktop strip inside a
// ~620px column (Monday 12734251677).

const PAGE = 'home';

/** The strip's resting gap between cards (Figma 12228:150815). */
const STRIP_GAP = 16;

/**
 * The PILED overlap, in `.piled` (`PiledUpJobs.module.css`) — each card pulled back onto the one
 * before it by a negative inline-start margin, since CSS `gap` cannot go negative.
 *
 * Figma 15545:337594 draws 253.33-wide cards stepping 119.33 — every card covers all but 47% of its
 * predecessor. Ours are 260 (`.cardReveal`), so the same ratio is a 122 step, i.e. a −138 margin.
 * Laying it out rather than transforming it is what keeps the rest of the strip honest: the row still
 * scrolls, the scroller still pages exactly one card per chevron press (it MEASURES the step off the
 * first two children), and DOM order alone puts the trailing card on top, as the design draws it.
 */

export function PiledUpJobs({
  jobs,
  narrow,
  piled = false,
}: {
  jobs: PiledUpJob[];
  narrow: boolean;
  /** Overlap the cards instead of spacing them — the band is carrying a third slot. */
  piled?: boolean;
}) {
  if (jobs.length === 0) return null;
  return narrow ? <PiledUpDeck jobs={jobs} /> : <PiledUpStrip jobs={jobs} piled={piled} />;
}

function PiledUpStrip({ jobs, piled }: { jobs: PiledUpJob[]; piled: boolean }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  return (
    <section aria-labelledby="piled-up-heading" className={styles.section}>
      {/* Header ABOVE the strip (Figma 13528:99200): the count-led title on the left, a "See all" text
          button on the right that opens the full Agent Tasks screen (/tasks). */}
      <header className={styles.header}>
        <Typography
          variant="h4"
          color="primary"
          component="h2"
          id="piled-up-heading"
          // 14/18 medium: sym-h4 already gives 14/18, but its weight is 600 — nudge to 500 (medium),
          // a step the DS type scale doesn't carry.
          style={{ fontWeight: 'var(--sym-weight-medium)' }}
          className={styles.heading}
        >
          {t(jobs.length === 1 ? 'home.tasks.titleOne' : 'home.tasks.titleOther', { count: jobs.length })}
        </Typography>
        <TextButton
          size="small"
          href="/tasks"
          endIcon={<ArrowUpRight size={16} className="dir-flip" aria-hidden />}
          onClick={() =>
            logPageClickBi({ page_name: PAGE, button_name: 'see_all_tasks' }, { componentName: 'see-all-tasks' })
          }
        >
          {t('home.tasks.seeAll')}
        </TextButton>
      </header>
      <HorizontalScroller
        ariaPrev={t('piledUp.scrollPrev')}
        ariaNext={t('piledUp.scrollNext')}
        gap={piled ? 0 : STRIP_GAP}
        rowStyle={{ paddingTop: 16, paddingBottom: 24, marginTop: -16, marginBottom: -24 }}
      >
        {jobs.map((job, i) => (
          <JobCard key={job.id} job={job} index={i} piled={piled} />
        ))}
      </HorizontalScroller>
    </section>
  );
}

function JobCard({ job, index, piled }: { job: PiledUpJob; index: number; piled: boolean }) {
  const { t } = useTranslation();
  const { openModal } = useTaskSidePanel();
  const { logPageClickBi } = useGenericBi();
  return (
    <div
      className={`${styles.cardReveal}${piled ? ` ${styles.piled}` : ''}`}
      style={{ '--stagger': index } as React.CSSProperties}
    >
      <Card
        variant="shadow"
        interactive
        component="button"
        onClick={() => {
          logPageClickBi(
            { page_name: PAGE, button_name: 'open_job' },
            job.id ? { entity: { id: job.id, type: ENTITY_TYPE.task } } : undefined,
          );
          openModal(<TaskDetailModal taskId={job.id} />, job.id);
        }}
        className={styles.card}
      >
        <span className={styles.meta}>
          {job.agentId ? `${job.agentName} · ${job.workspaceName}` : job.workspaceName}
        </span>
        <span className={styles.title}>{job.title}</span>
        <span className={styles.bullseye}>
          <PiledUpAvatar job={job} size={180} />
        </span>
      </Card>
    </div>
  );
}
